"use client";

import { lazy, Suspense, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import type { Editor } from "@tiptap/core";
import { Check, Copy, Download, ExternalLink, FileText, Pencil, Save, X } from "lucide-react";
import { useTranslation } from "react-i18next";

import { fetchSingleFile, type SingleFileResponse } from "@/api/file-browser";
import { SingleHtmlPreview } from "@/components/views/single-html-preview";
import { viewRefKey, type ViewRef } from "@/api/views";
import { MarkdownOutline, MarkdownOutlineTop } from "@/components/markdown-outline";
import { MarkdownViewer } from "@/components/markdown-viewer";
import { MarkdownViewerToolbar } from "@/components/markdown-viewer-toolbar";
import { MarkdownViewerSaveStatus } from "@/components/markdown-viewer-save-status";
import { CollaborativeMarkdownPanel, useCollabCapability } from "@/components/collaborative-markdown-panel";
import { ImageViewer } from "@/components/image-viewer";
import { HtmlViewer } from "@/components/html-viewer";
import { ImageViewerToolbar } from "@/components/image-viewer-toolbar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { useMarkdownFile } from "@/hooks/use-markdown-file";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import { useSignedTextFile } from "@/hooks/use-signed-text-file";
import { useHtmlFile } from "@/hooks/use-html-file";
import { resolveCodeEditorLanguage } from "@/components/code-file-editor-language";
import { extractFileToken, isImageFileName, isXlsxFileName, xlsxPreviewUrl } from "@/lib/file-links";
import { useMarkdownCrossFileProjection } from "@/lib/markdown-cross-file-links";
import type { AccessTokenGetter } from "@/lib/utils";

const CodeFileEditor = lazy(async () => {
  const module = await import("@/components/code-file-editor");
  return { default: module.CodeFileEditor };
});

interface SingleFileViewSharedProps {
  className?: string;
  /** Configured view title, shown until the document metadata has loaded. */
  fallbackTitle?: string;
  /** Panel close affordance; omit on standalone routes that use a Back control. */
  onClose?: () => void;
  /** Opens this view's standalone route from an embedded panel. */
  onOpenInNewTab?: () => void;
  /** Collapses lower-priority document actions into an overflow menu. */
  compact?: boolean;
  /** Optional leading control, such as the standalone route's Back button. */
  leading?: ReactNode;
  /** Secondary agent or session context for a standalone route. */
  contextTitle?: string;
}

interface ConfiguredSingleFileViewProps extends SingleFileViewSharedProps {
  viewRef: ViewRef;
  getToken: AccessTokenGetter;
  /** Presentation intent from a safe view summary; live capability still wins. */
  openInEdit?: boolean;
  /** A newer conversation-pinned editor contract is unavailable on this server. */
  pinnedFileEditingUnsupported?: boolean;
}

interface SignedSourceSingleFileViewProps extends SingleFileViewSharedProps {
  /** Read-only signed ``/files/d/`` URL supplied by an agent chat link. */
  sourceUrl: string;
  /** Filename decoded from the signed link (or its safe fallback). */
  sourceFileName: string;
}

export type SingleFileViewProps =
  | ConfiguredSingleFileViewProps
  | SignedSourceSingleFileViewProps;

/**
 * One Markdown, source, or image document configured as a view, pinned from
 * the viewer, or opened directly from a signed source chat link.
 *
 * It opens read-only by default. Files with a live write capability expose an
 * Edit control so users can explicitly enter the editable working surface.
 */
export function SingleFileView(props: SingleFileViewProps) {
  if ("sourceUrl" in props) return <SignedSourceSingleFileView {...props} />;
  return <ConfiguredSingleFileView {...props} />;
}

function ConfiguredSingleFileView({
  viewRef,
  getToken,
  openInEdit = false,
  pinnedFileEditingUnsupported = false,
  className,
  fallbackTitle,
  onClose,
  onOpenInNewTab,
  compact = false,
  leading,
  contextTitle,
}: ConfiguredSingleFileViewProps) {
  const { t } = useTranslation();
  const viewKey = viewRefKey(viewRef);
  const [page, setPage] = useState<SingleFileResponse | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageRetry, setPageRetry] = useState(0);
  const [htmlMode, setHtmlMode] = useState<"preview" | "source">("preview");
  const [editor, setEditor] = useState<Editor | null>(null);
  const [editable, setEditable] = useState(false);
  const [savedMarkdown, setSavedMarkdown] = useState<string | null>(null);
  const [currentMarkdown, setCurrentMarkdown] = useState<string | null>(null);
  const [editingSource, setEditingSource] = useState(false);
  const [sourceDraft, setSourceDraft] = useState<string | null>(null);
  const [sourceDiagnostic, setSourceDiagnostic] = useState(false);
  const [markdownWriteBlocked, setMarkdownWriteBlocked] = useState(false);
  const [sourceWriteBlocked, setSourceWriteBlocked] = useState(false);
  const [leaveRequested, setLeaveRequested] = useState(false);
  const [collabUnavailable, setCollabUnavailable] = useState(false);
  const { copied: sourceCopied, copy: copySourceDraft } = useCopyToClipboard();
  const { copied: markdownCopied, copy: copyMarkdownDraft } = useCopyToClipboard();
  const currentMarkdownRef = useRef<string | null>(null);
  const contentScrollRef = useRef<HTMLDivElement>(null);
  const preserveDraftOnRetryRef = useRef(false);
  const dirty = (
    savedMarkdown !== null
    && currentMarkdown !== null
    && savedMarkdown !== currentMarkdown
  );

  const refreshEditCapability = useCallback((kind: "markdown" | "source") => {
    preserveDraftOnRetryRef.current = true;
    if (kind === "markdown") setMarkdownWriteBlocked(true);
    else setSourceWriteBlocked(true);
    setPageRetry((value) => value + 1);
  }, []);

  useEffect(() => {
    const preserveDraft = preserveDraftOnRetryRef.current;
    preserveDraftOnRetryRef.current = false;
    let cancelled = false;
    if (!preserveDraft) setPage(null);
    setPageLoading(true);
    setPageError(null);
    if (!preserveDraft) {
      setHtmlMode("preview");
      setEditable(false);
      setSavedMarkdown(null);
      setCurrentMarkdown(null);
      setEditingSource(false);
      setSourceDraft(null);
      setMarkdownWriteBlocked(false);
      setSourceWriteBlocked(false);
    }
    setSourceDiagnostic(false);
    if (!preserveDraft) {
      setLeaveRequested(false);
      currentMarkdownRef.current = null;
    }

    void fetchSingleFile(viewRef, getToken)
      .then((response) => {
        if (cancelled) return;
        setPage(response);
        if (preserveDraft) {
          setMarkdownWriteBlocked(false);
          setSourceWriteBlocked(false);
          if (!response.can_write || !response.edit_url) {
            if (response.is_markdown) setEditable(false);
            else setEditingSource(false);
          }
        } else {
          // The flag requests presentation only; the freshly fetched live
          // capability remains the authority for entering edit mode.
          setEditable(Boolean(
            openInEdit
            && response.is_markdown
            && response.can_write
            && response.edit_url
            && !pinnedFileEditingUnsupported,
          ));
        }
        setPageLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setPageError(error instanceof Error ? error.message : t("singleFile.unableToLoad"));
        setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, openInEdit, pageRetry, pinnedFileEditingUnsupported, t, viewKey]);

  const markdownReadUrl = page?.is_markdown ? page.download_url : null;
  const markdownRoomTokenUrl = page?.is_markdown && !pinnedFileEditingUnsupported ? page.edit_url : null;
  const capability = useCollabCapability(
    markdownReadUrl && markdownRoomTokenUrl ? markdownReadUrl : "",
    getToken,
    page?.name ?? "",
    markdownRoomTokenUrl ?? undefined,
  );
  const collabActive = Boolean(markdownRoomTokenUrl
    && capability.kind === "available" && !collabUnavailable);
  const collabLoading = Boolean(markdownRoomTokenUrl
    && markdownReadUrl && extractFileToken(markdownReadUrl)
    && capability.kind === "loading" && !collabUnavailable);
  useEffect(() => { setCollabUnavailable(false); }, [viewKey, markdownReadUrl]);
  const handleCollabUnavailable = useCallback(() => setCollabUnavailable(true), []);
  const legacyMarkdownReadUrl = collabActive || collabLoading ? null : markdownReadUrl;
  const {
    content: signedContent,
    save,
    saving,
    saveError,
    conflict: markdownConflict,
    remoteChange: markdownRemoteChange,
    saveOutcomeUnknown: markdownSaveOutcomeUnknown,
    checkLatest: checkMarkdownSaveOutcome,
    reloadLatest: reloadMarkdown,
  } = useMarkdownFile(legacyMarkdownReadUrl, {
    documentIdentity: viewKey,
    // Keep checking a Markdown draft while it is editable, but do not let a
    // background GET replace it. A stale Save still receives the server's
    // If-Match conflict response.
    pollingPaused: false,
    detectOnly: editable || dirty,
    writeUrl: page?.is_markdown && !pinnedFileEditingUnsupported && !collabActive && !collabLoading ? page.edit_url : null,
    onExpired: () => refreshEditCapability("markdown"),
  });
  const content = signedContent ?? page?.content ?? null;
  const renderContent = useMarkdownCrossFileProjection(
    page?.is_markdown && !collabActive && !collabLoading ? content : null,
    markdownReadUrl ?? "",
    viewRef.scope === "conversation" ? viewRef.sessionId : null,
    getToken,
    viewRef,
  );
  const canEdit = Boolean(
    page?.can_write
    && page.edit_url
    && !markdownWriteBlocked
    && !pinnedFileEditingUnsupported
  );
  const workbookFile = isXlsxFileName(page?.name);
  const sourceFile = !workbookFile && (page?.content_kind === "code" || page?.content_kind === "text");
  const htmlFile = Boolean(page && /\.html?$/i.test(page.name));
  const workbookPreview = useHtmlFile(workbookFile && page ? xlsxPreviewUrl(page.download_url) : null);
  const pageIsImage = Boolean(
    page && (page.content_kind === "image" || isImageFileName(page.name)),
  );
  const {
    content: signedSourceContent,
    loading: sourceLoading,
    error: sourceError,
    saving: sourceSaving,
    saveError: sourceSaveError,
    conflict: sourceConflict,
    remoteChange: sourceRemoteChange,
    saveOutcomeUnknown: sourceSaveOutcomeUnknown,
    save: saveSource,
    checkLatest: checkSourceSaveOutcome,
    reloadLatest: reloadSource,
  } = useSignedTextFile(
    sourceFile ? page?.download_url ?? null : null,
    sourceFile && !pinnedFileEditingUnsupported ? page?.edit_url ?? null : null,
    {
      contentType: page?.text_mime_type,
      refreshPolicy: editingSource && sourceDraft !== null ? "detect-only" : "auto",
      documentIdentity: viewKey,
    },
  );
  const sourceContent = sourceDraft ?? signedSourceContent;
  const sourceDirty = Boolean(
    editingSource
    && sourceDraft != null
    && signedSourceContent != null
    && sourceDraft !== signedSourceContent,
  );
  // A configured single-file view can be either Markdown or source text.
  // Keep one close/escape/browser-leave guard for both so a Markdown draft
  // cannot be discarded merely because it uses the older Tiptap surface.
  const hasUnsavedWork = sourceDirty || (!collabActive && !collabLoading && dirty);
  const sourceCanEdit = Boolean(
    sourceFile
    && page?.can_write
    && page.edit_url
    && !sourceWriteBlocked
    && !pinnedFileEditingUnsupported
  );

  useEffect(() => {
    if (!pinnedFileEditingUnsupported) return;
    setEditable(false);
    setEditingSource(false);
  }, [pinnedFileEditingUnsupported]);
  const sourceLanguageLabel = page?.language === "yaml"
    ? t("singleFile.language.yaml")
    : page?.language === "json"
      ? t("singleFile.language.json")
      : page?.language === "javascript"
        ? t("singleFile.language.javascript")
        : t("singleFile.language.plaintext");

  useEffect(() => {
    if (!editingSource && signedSourceContent != null) setSourceDraft(signedSourceContent);
  }, [editingSource, signedSourceContent]);

  useEffect(() => {
    if (!hasUnsavedWork) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsavedWork]);

  useEffect(() => {
    if (!hasUnsavedWork) return;
    const guardEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopPropagation();
      setLeaveRequested(true);
    };
    window.addEventListener("keydown", guardEscape, true);
    return () => window.removeEventListener("keydown", guardEscape, true);
  }, [hasUnsavedWork]);

  const handleMarkdownLoaded = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setSavedMarkdown(markdown);
    setCurrentMarkdown(markdown);
  }, []);

  const handleMarkdownChange = useCallback((markdown: string) => {
    currentMarkdownRef.current = markdown;
    setCurrentMarkdown(markdown);
  }, []);

  const handleSave = useCallback(async (markdown?: string): Promise<boolean> => {
    if (markdownWriteBlocked || pinnedFileEditingUnsupported) return false;
    const markdownToSave = markdown ?? currentMarkdown;
    if (markdownToSave === null) return false;
    if (markdownToSave === savedMarkdown) return true;

    const didSave = await save(markdownToSave);
    if (didSave) {
      setSavedMarkdown(markdownToSave);
      const latestMarkdown = currentMarkdownRef.current ?? markdownToSave;
      currentMarkdownRef.current = latestMarkdown;
      setCurrentMarkdown(latestMarkdown);
    }
    return didSave;
  }, [currentMarkdown, markdownWriteBlocked, pinnedFileEditingUnsupported, savedMarkdown, save]);

  const handleSourceSave = useCallback(async () => {
    if (
      sourceDraft === null
      || sourceDiagnostic
      || sourceSaving
      || sourceConflict
      || sourceSaveOutcomeUnknown
      || pinnedFileEditingUnsupported
    ) return false;
    const result = await saveSource(sourceDraft);
    if (result.kind === "saved") {
      setSourceDraft(sourceDraft);
      return true;
    }
    if (result.kind === "expired") {
      refreshEditCapability("source");
    } else if (
      result.kind === "missing"
      || result.kind === "denied"
      || result.kind === "too_large"
    ) {
      setSourceWriteBlocked(true);
    }
    return false;
  }, [
    refreshEditCapability,
    saveSource,
    sourceConflict,
    sourceDiagnostic,
    sourceDraft,
    sourceSaveOutcomeUnknown,
    sourceSaving,
    pinnedFileEditingUnsupported,
  ]);

  const cancelSourceEdit = useCallback(() => {
    if (sourceDirty && !window.confirm(t("singleFile.discardPrompt"))) return;
    setSourceDraft(signedSourceContent);
    setEditingSource(false);
  }, [signedSourceContent, sourceDirty, t]);

  const reloadSourceAndDiscard = useCallback(async () => {
    if (!window.confirm(t("singleFile.reloadDiscardPrompt"))) return;
    const reloaded = await reloadSource();
    if (reloaded) {
      setSourceDraft(null);
      setEditingSource(false);
    }
  }, [reloadSource, t]);

  const reloadMarkdownAndDiscard = useCallback(async () => {
    if (!window.confirm(t("singleFile.reloadDiscardPrompt"))) return;
    const wasEditable = editable;
    // Tiptap deliberately ignores external content while in edit mode. Turn
    // off editing before the no-store reload so this explicit destructive
    // action, unlike a background poll, replaces the local draft.
    setEditable(false);
    const latest = await reloadMarkdown();
    if (latest === null) {
      setEditable(wasEditable);
      return;
    }
    currentMarkdownRef.current = latest;
    setSavedMarkdown(latest);
    setCurrentMarkdown(latest);
  }, [editable, reloadMarkdown, t]);

  const reconcileSourceSave = useCallback(async () => {
    await checkSourceSaveOutcome();
  }, [checkSourceSaveOutcome]);

  const reconcileMarkdownSave = useCallback(async () => {
    const result = await checkMarkdownSaveOutcome();
    if (result.kind !== "reconciled") return;
    setSavedMarkdown(result.content);
    if (currentMarkdownRef.current === null) {
      currentMarkdownRef.current = result.content;
      setCurrentMarkdown(result.content);
    }
  }, [checkMarkdownSaveOutcome]);

  const requestClose = useCallback(() => {
    if (hasUnsavedWork) {
      setLeaveRequested(true);
      return;
    }
    onClose?.();
  }, [hasUnsavedWork, onClose]);

  const saveThenClose = useCallback(async () => {
    const saved = sourceDirty
      ? await handleSourceSave()
      : dirty
        ? await handleSave()
        : true;
    if (saved) {
      setLeaveRequested(false);
      onClose?.();
    }
  }, [dirty, handleSave, handleSourceSave, onClose, sourceDirty]);

  const fileName = page?.name ?? "document.md";
  const title = page?.title ?? fallbackTitle ?? fileName;

  if (collabActive && capability.kind === "available" && markdownReadUrl) {
    return <section className={`h-full min-h-0 ${className ?? ""}`} data-testid="single-file-view">
      <CollaborativeMarkdownPanel
        embedded
        room={capability.room}
        url={markdownReadUrl}
        roomTokenUrl={markdownRoomTokenUrl ?? undefined}
        fileName={fileName}
        getToken={getToken}
        onClose={() => onClose?.()}
        onUnavailable={handleCollabUnavailable}
        leading={leading}
        contextTitle={contextTitle}
        onOpenInNewTab={onOpenInNewTab}
        hideClose={!onClose}
      />
    </section>;
  }

  if (collabLoading) {
    return <section className={`flex h-full items-center justify-center bg-background ${className ?? ""}`}
      role="status" aria-label={t("singleFile.loading")}>
      <Spinner className="size-5" />
    </section>;
  }

  return (
    <section
      className={`flex h-full min-w-0 flex-col bg-background ${className ?? ""}`}
      data-testid="single-file-view"
    >
      <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {leading}
          <FileText
            className="size-4 shrink-0 text-muted-foreground"
            data-testid="single-file-document-icon"
          />
          {page?.is_markdown && (
            <MarkdownViewerSaveStatus
              contentLoaded={currentMarkdown != null}
              editable={editable}
              hasSaveHandler={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown}
              dirty={dirty}
              saving={saving}
              saveError={saveError}
            />
          )}
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold" title={title}>{title}</span>
            {contextTitle && (
              <span className="block truncate text-xs text-muted-foreground" title={contextTitle}>
                {contextTitle}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {page?.is_markdown ? (
            <MarkdownViewerToolbar
              editor={editor}
              fileName={fileName}
              getToken={getToken}
              fileUrl={markdownReadUrl ?? undefined}
              onOpenInNewWindow={onOpenInNewTab}
              editable={editable}
              dirty={dirty}
              saving={saving}
              saveError={saveError}
              saveBlocked={Boolean(markdownWriteBlocked || markdownConflict || markdownSaveOutcomeUnknown)}
              onToggleEdit={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown ? () => setEditable((value) => !value) : undefined}
              onSave={canEdit && !markdownConflict && !markdownSaveOutcomeUnknown ? handleSave : undefined}
              contentVersion={currentMarkdown}
              compact={compact}
            />
          ) : (sourceFile || htmlFile || workbookFile) && page ? (
            <>
              {htmlFile && <div className="flex gap-1" role="group">
                <Button variant={htmlMode === "preview" ? "secondary" : "ghost"} size="sm" onClick={() => setHtmlMode("preview")}>{t("singleFile.htmlPreview")}</Button>
                <Button variant={htmlMode === "source" ? "secondary" : "ghost"} size="sm" onClick={() => setHtmlMode("source")}>{t("singleFile.showSource")}</Button>
              </div>}
              {!workbookFile && <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{sourceLanguageLabel}</span>}
              <Button asChild variant="ghost" size="icon-sm" aria-label={t("viewer.downloadContent")}>
                <a href={page.download_url} download={fileName}><Download className="size-4" /></a>
              </Button>
              {onOpenInNewTab && (
                <Button variant="ghost" size="icon-sm" onClick={onOpenInNewTab} aria-label={t("viewer.openInNewWindow")}>
                  <ExternalLink className="size-4" />
                </Button>
              )}
              {sourceCanEdit && !editingSource && (
                <Button variant="outline" size="sm" onClick={() => setEditingSource(true)} data-testid="single-file-source-edit">
                  <Pencil className="mr-1 size-3.5" />{t("singleFile.editCode")}
                </Button>
              )}
              {editingSource && !pinnedFileEditingUnsupported && (
                <>
                  <Button variant="outline" size="sm" onClick={cancelSourceEdit} disabled={sourceSaving}>{t("singleFile.cancelEditing")}</Button>
                  <Button size="sm" onClick={() => void handleSourceSave()} disabled={!sourceDirty || sourceSaving || sourceDiagnostic || sourceWriteBlocked || Boolean(sourceConflict) || sourceSaveOutcomeUnknown} data-testid="single-file-source-save">
                    <Save className="mr-1 size-3.5" />{sourceSaving ? t("viewer.savingChanges") : t("common:actions.save")}
                  </Button>
                </>
              )}
            </>
          ) : pageIsImage && page ? (
            <ImageViewerToolbar
              fileUrl={page.download_url}
              fileName={fileName}
              onOpenInNewWindow={onOpenInNewTab}
            />
          ) : null}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={requestClose}
              aria-label={t("viewer.closeViewer")}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </header>

      {pinnedFileEditingUnsupported && (
        <p
          className="mx-3 mt-3 rounded border border-amber-500/50 bg-amber-500/5 px-3 py-2 text-sm text-amber-900 dark:text-amber-200"
          role="alert"
        >
          {t("views.pinnedFileEditingUnsupported")}
        </p>
      )}

      <div className="relative min-h-0 flex-1 overflow-hidden">
        <div ref={contentScrollRef} className="h-full min-w-0 overflow-y-auto overflow-x-hidden p-4">
          {pageLoading && !page ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <Spinner className="size-5" />
              <p className="text-sm">{t("singleFile.loading")}</p>
            </div>
          ) : pageError && !page ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
              <p className="text-sm text-destructive">{pageError}</p>
              <Button variant="outline" size="sm" onClick={() => setPageRetry((value) => value + 1)}>
                {t("common:actions.retry")}
              </Button>
            </div>
          ) : pageIsImage && page ? (
            <ImageViewer url={page.download_url} fileName={fileName} />
          ) : workbookFile && page ? (
            workbookPreview.loading ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t("singleFile.loading")}</div>
              : workbookPreview.error ? <div className="flex h-full flex-col items-center justify-center gap-3 text-center" role="alert"><p className="text-sm text-destructive">{workbookPreview.error}</p><Button asChild variant="outline" size="sm"><a href={page.download_url} download={fileName}>{t("viewer.downloadContent")}</a></Button></div>
                : workbookPreview.content != null ? <HtmlViewer content={workbookPreview.content} fileName={fileName} /> : null
          ) : htmlFile && htmlMode === "preview" && page ? (
            <SingleHtmlPreview viewRef={viewRef} getToken={getToken} fileName={fileName} />
          ) : htmlFile && !sourceFile && page ? (
            <div className="flex flex-col items-start gap-2 text-sm">
              <p>{t("singleFile.unableToLoad")}</p>
              <Button asChild variant="outline" size="sm">
                <a href={page.download_url} download={fileName}>{t("viewer.downloadContent")}</a>
              </Button>
            </div>
          ) : sourceFile ? (
            <div className="flex h-full min-h-0 flex-col">
              {sourceLoading && sourceContent == null ? (
                <div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t("singleFile.loading")}</div>
              ) : sourceError ? (
                <div className="flex h-full items-center justify-center text-sm text-destructive">{sourceError}</div>
              ) : sourceContent != null && page ? (
                <>
                  {sourceRemoteChange && !sourceConflict && !sourceSaveOutcomeUnknown && <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">{t("singleFile.remoteChange")}</p>}
                  {sourceSaveOutcomeUnknown && (
                    <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                      <span>{t("singleFile.saveOutcomeUnknown")}</span>
                      <Button size="sm" variant="outline" onClick={() => void reconcileSourceSave()}>{t("singleFile.checkLatest")}</Button>
                    </div>
                  )}
                  {sourceConflict && (
                    <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                      <span>{t("singleFile.conflict")}</span>
                      <div className="flex shrink-0 gap-1">
                        <Button size="sm" variant="outline" onClick={() => void copySourceDraft(sourceDraft ?? sourceContent)} aria-label={t("singleFile.copyLocalContent")}>
                          {sourceCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => void reloadSourceAndDiscard()}>{t("singleFile.reloadAndDiscard")}</Button>
                      </div>
                    </div>
                  )}
                  {sourceSaveError && <p className="mb-2 text-xs text-destructive" role="alert">{sourceSaveError}</p>}
                  {sourceDiagnostic && editingSource && <p className="mb-2 text-xs text-destructive" role="alert">{t("singleFile.syntaxError")}</p>}
                  <Suspense fallback={<div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">{t("singleFile.loading")}</div>}>
                    <CodeFileEditor
                      documentKey={page.name}
                      value={sourceContent}
                      language={page.language}
                      readOnly={!editingSource || pinnedFileEditingUnsupported}
                      onChange={setSourceDraft}
                      onSave={() => void handleSourceSave()}
                      onDiagnosticsChange={setSourceDiagnostic}
                      className="min-h-0 flex-1"
                    />
                  </Suspense>
                </>
              ) : null}
            </div>
          ) : content != null ? (
            <>
              {page?.is_markdown && markdownRemoteChange && !markdownConflict && !markdownSaveOutcomeUnknown && (
                <p className="mb-2 rounded bg-amber-100 px-2 py-1 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-200">
                  {t("singleFile.remoteChange")}
                </p>
              )}
              {page?.is_markdown && markdownSaveOutcomeUnknown && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-amber-500/50 bg-amber-500/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("singleFile.saveOutcomeUnknown")}</span>
                  <Button size="sm" variant="outline" onClick={() => void reconcileMarkdownSave()}>{t("singleFile.checkLatest")}</Button>
                </div>
              )}
              {page?.is_markdown && markdownConflict && (
                <div className="mb-2 flex items-center justify-between gap-2 rounded border border-destructive/50 bg-destructive/5 px-2 py-1.5 text-xs" role="alert">
                  <span>{t("singleFile.conflict")}</span>
                  <div className="flex shrink-0 gap-1">
                    <Button size="sm" variant="outline" onClick={() => void copyMarkdownDraft(currentMarkdown ?? content)} aria-label={t("singleFile.copyLocalContent")}>
                      {markdownCopied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => void reloadMarkdownAndDiscard()}>{t("singleFile.reloadAndDiscard")}</Button>
                  </div>
                </div>
              )}
              <MarkdownOutlineTop
                editor={editor}
                scrollContainerRef={contentScrollRef}
                contentVersion={currentMarkdown ?? content}
              />
              <MarkdownViewer
                key={page?.name ?? "single-file"}
                content={content}
                renderContent={!editable && !dirty ? renderContent : null}
                fileName={fileName}
                editable={editable}
                saving={saving}
                onEditorReady={setEditor}
                onMarkdownLoaded={handleMarkdownLoaded}
                onMarkdownChange={handleMarkdownChange}
              />
            </>
          ) : page ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-4 text-center">
              <p className="text-sm text-muted-foreground">{page.size_bytes > 1024 * 1024 ? t("singleFile.tooLarge") : t("singleFile.unsupported")}</p>
              <Button asChild variant="outline" size="sm">
                <a href={page.download_url} download={fileName}><Download className="mr-1 size-4" />{t("viewer.downloadContent")}</a>
              </Button>
            </div>
          ) : null}
        </div>
        {page?.is_markdown && (
          <MarkdownOutline
            editor={editor}
            scrollContainerRef={contentScrollRef}
            contentVersion={currentMarkdown ?? content}
          />
        )}
      </div>
      {leaveRequested && (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/80 p-4" role="dialog" aria-modal="true" aria-label={t("singleFile.unsavedChanges")}>
          <Card className="max-w-sm bg-background p-4 shadow-lg">
            <p className="text-sm font-medium">{t("singleFile.unsavedChanges")}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t("singleFile.discardPrompt")}</p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setLeaveRequested(false)}>{t("singleFile.keepEditing")}</Button>
              <Button size="sm" onClick={() => void saveThenClose()} disabled={sourceSaving || sourceDiagnostic || sourceWriteBlocked || markdownWriteBlocked || Boolean(sourceConflict) || sourceSaveOutcomeUnknown || Boolean(markdownConflict) || markdownSaveOutcomeUnknown}>{t("singleFile.saveChanges")}</Button>
              <Button variant="destructive" size="sm" onClick={() => { setLeaveRequested(false); onClose?.(); }}>{t("singleFile.discard")}</Button>
            </div>
          </Card>
        </div>
      )}
    </section>
  );
}

/**
 * Read a source file directly from an agent-chat signed download link.
 *
 * Unlike a configured or pinned view, this surface has no authenticated
 * metadata endpoint and deliberately receives no write capability. The
 * signed URL is used only for read/HEAD requests, while CodeMirror remains
 * permanently read-only.
 */
function SignedSourceSingleFileView({
  sourceUrl,
  sourceFileName,
  className,
  fallbackTitle,
  onClose,
  onOpenInNewTab,
  leading,
  contextTitle,
}: SignedSourceSingleFileViewProps) {
  const { t } = useTranslation();
  const {
    content,
    loading,
    error,
    reloadLatest,
  } = useSignedTextFile(sourceUrl, null, {
    refreshPolicy: "auto",
    documentIdentity: sourceFileName,
  });
  const language = resolveCodeEditorLanguage(undefined, sourceFileName);
  const languageLabel = language === "yaml"
    ? t("singleFile.language.yaml")
    : language === "json"
      ? t("singleFile.language.json")
      : language === "javascript"
        ? t("singleFile.language.javascript")
        : t("singleFile.language.plaintext");
  const title = fallbackTitle ?? sourceFileName;

  return (
    <section
      className={`flex h-full min-w-0 flex-col bg-background ${className ?? ""}`}
      data-testid="single-file-view"
    >
      <header className="flex min-h-[53px] shrink-0 items-center justify-between gap-2 border-b px-3 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          {leading}
          <FileText
            className="size-4 shrink-0 text-muted-foreground"
            data-testid="single-file-document-icon"
          />
          <div className="min-w-0">
            <span className="block truncate text-sm font-semibold" title={title}>{title}</span>
            {contextTitle && (
              <span className="block truncate text-xs text-muted-foreground" title={contextTitle}>
                {contextTitle}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">{languageLabel}</span>
          <Button asChild variant="ghost" size="icon-sm" aria-label={t("viewer.downloadContent")}>
            <a href={sourceUrl} download={sourceFileName}><Download className="size-4" /></a>
          </Button>
          {onOpenInNewTab && (
            <Button variant="ghost" size="icon-sm" onClick={onOpenInNewTab} aria-label={t("viewer.openInNewWindow")}>
              <ExternalLink className="size-4" />
            </Button>
          )}
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              className="size-7 shrink-0"
              onClick={onClose}
              aria-label={t("viewer.closeViewer")}
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-hidden p-4">
        {loading && content == null ? (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {t("singleFile.loading")}
          </div>
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void reloadLatest()}>
              {t("common:actions.retry")}
            </Button>
          </div>
        ) : content != null ? (
          <Suspense fallback={<div className="flex h-full items-center justify-center text-sm text-muted-foreground">{t("singleFile.loading")}</div>}>
            <CodeFileEditor
              documentKey={sourceFileName}
              value={content}
              language={language}
              readOnly={true}
              className="h-full min-h-0"
            />
          </Suspense>
        ) : null}
      </div>
    </section>
  );
}
